import React, { useState } from 'react';
import type { AudioFile } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useIsFirstLoginSession } from '../utils/firstLoginDetect';
import { useClaimedFilesPrompt } from './ClaimedFilesCollectionPrompt';
import { firstLoginUpgradeHeroSpec } from './FirstLoginUpgradeHero';
import { freeUserVideoRenderCardSpec } from './FreeUserVideoRenderCard';
import { expiringFilesBannerSpec } from './ExpiringFilesBanner';
import type {
  BannerCategory,
  BannerInputs,
  BannerSpec,
} from './banners/types';

/**
 * The single card that occupies the Home dashboard's top slot for free users.
 *
 * Two pre-emptions, then a category-ordered resolution against a registry of
 * specs:
 *   1. Not a free authenticated user on the dashboard → nothing.
 *   2. Post-claim "organize into a collection" prompt is being shown
 *      elsewhere on this page → nothing (don't stack primary nudges).
 *   3. Walk the registry in category order (welcome → educational → nudge).
 *      First spec whose `when` fires gets to render. None match → nothing.
 *
 * Add the next banner:
 *   1. Write the component.
 *   2. Export its `BannerSpec` next to the default export.
 *   3. Add the spec to the registry below.
 * No edits to the resolution logic.
 */

/** Categories resolve in this order. Welcome beats educational beats nudge. */
const CATEGORY_ORDER: readonly BannerCategory[] = [
  'welcome',
  'educational',
  'nudge',
];

/**
 * The registry. Order within a category matters when two specs share a
 * category — first match wins — but across categories the order in this
 * array is irrelevant; CATEGORY_ORDER is the tiebreaker.
 */
const REGISTRY: readonly BannerSpec[] = [
  firstLoginUpgradeHeroSpec,
  freeUserVideoRenderCardSpec,
  expiringFilesBannerSpec,
];

/** Pure: pick the highest-priority spec whose predicate matches, or null. */
function pickBanner(
  specs: readonly BannerSpec[],
  inputs: BannerInputs,
): BannerSpec | null {
  for (const category of CATEGORY_ORDER) {
    const match = specs.find((s) => s.category === category && s.when(inputs));
    if (match) return match;
  }
  return null;
}

export interface HomeTopSlotProps {
  /** Files for this authenticated user (already loaded). */
  userFiles: AudioFile[];
  /** Subset of userFiles expiring within 24h. */
  expiringFiles: AudioFile[];
  /** Count of expiringFiles — passed through to spec inputs. */
  expiringCount: number;
  /** True while the initial files fetch is still pending — slot stays empty. */
  loadingUserFiles: boolean;
}

const HomeTopSlot: React.FC<HomeTopSlotProps> = ({
  userFiles,
  expiringFiles,
  expiringCount,
  loadingUserFiles,
}) => {
  const { isAuthenticated, isProUser, isBetaUser, getUserId } = useAuth();
  const { prompt: claimedFilesPrompt } = useClaimedFilesPrompt();

  // Capture "is this a first-login session?" once per userId for the lifetime
  // of the React app's session. The welcome card writes its own seen-flag on
  // mount via `markFirstLoginSeen`; if we used the live `isFirstLogin` here,
  // the next re-render (e.g. when Home's userFiles refetch settles) would
  // re-evaluate the spec's predicate, see the flag was just written, and
  // collapse the slot — flashing the card for one frame. Freezing the value
  // at first read keeps the card mounted for the duration of the page view.
  const isFirstLoginSession = useIsFirstLoginSession();

  // Per-mount dismissal for the educational video card. X-click flips this
  // for the current Home view; re-navigating to Home resets it. Persistent
  // dismissal (Download click) lives in localStorage inside the card itself.
  const [videoCardDismissed, setVideoCardDismissed] = useState(false);

  // Single free-tier gate. Lives once, in one place, so the duplicated
  // copy that used to wrap both the slot and the expiring banner is gone.
  const freeUserOnDashboard =
    isAuthenticated && !isProUser() && !isBetaUser() && !loadingUserFiles;
  if (!freeUserOnDashboard) return null;

  // Page-local pre-emption: the post-claim prompt and the top slot would
  // compete for the same vertical attention, and the prompt is render-only
  // for ~one navigation. Yield while it's showing.
  if (claimedFilesPrompt) return null;

  const inputs: BannerInputs = {
    userId: getUserId() ?? '',
    isFirstLoginSession,
    userFiles,
    expiringFiles,
    expiringCount,
    videoCardDismissed,
    onDismissVideoCard: () => setVideoCardDismissed(true),
  };

  const banner = pickBanner(REGISTRY, inputs);
  return <>{banner ? banner.render(inputs) : null}</>;
};

export default HomeTopSlot;
